import React from 'react';
import { Watch, LogOut, Crown } from 'lucide-react';
import { PersonalInfoDialog } from './PersonalInfoDialog';
import { MealtimesDialog } from './MealtimesDialog';
import { BodyInfoDialog } from './BodyInfoDialog';
import { InvoicesDialog } from './InvoicesDialog';
import { DataPrivacyDialog } from './DataPrivacyDialog';
import SecurityDialog from './SecurityDialog';
import { ThemeDialog } from './ThemeDialog';
import { SubscriptionDialog } from './SubscriptionDialog';

export const PersonalDialogContent = ({
  // Personal Info
  isAuthenticated,
  profileName,
  setProfileName,
  profileEmail,
  profileCity,
  setProfileCity,
  profileCountry,
  setProfileCountry,
  savingProfile,
  saveProfile,
  // Mealtimes
  userMealTimes,
  setUserMealTimes,
  customMealName,
  setCustomMealName,
  savingMealTimes,
  setSavingMealTimes,
  // Body Info
  newBodyweight,
  setNewBodyweight,
  weightUnit,
  setWeightUnit,
  waistMeasurement,
  setWaistMeasurement,
  waistUnit,
  setWaistUnit,
  bodyHeight,
  setBodyHeight,
  heightUnit,
  setHeightUnit,
  userAge,
  setUserAge,
  userGender,
  setUserGender,
  strideLength,
  setStrideLength,
  onUpdateBodyweight,
  // Medications toggle
  medicationsEnabled,
  setMedicationsEnabled,
  // Weight goal
  weightGoal,
  setWeightGoal,
  weightGoalStartDate,
  setWeightGoalStartDate,
  // Diabetes settings
  diabetesEnabled,
  setDiabetesEnabled,
  diabetesUnit,
  setDiabetesUnit,
  diabetesInsulin,
  setDiabetesInsulin,
  diabetesType,
  setDiabetesType,
  // Invoices
  paymentHistory,
  loadingHistory,
  downloadingInvoice,
  onLoadHistory,
  onDownloadInvoice,
  // Data Privacy
  loadingDataSummary,
  dataSummary,
  onLoadDataSummary,
  downloadingData,
  onDownloadAllData,
  emailingPolicy,
  onEmailGdprPolicy,
  onDownloadGdprPdf,
  deletingData,
  onDeleteAllData,
  // Actions
  onNavigateWearables,
  onLogout,
  isExpired
}) => {
  return (
    <div className="grid grid-cols-2 gap-3 pt-4">
      {/* Personal Info */}
      <PersonalInfoDialog
        isAuthenticated={isAuthenticated}
        profileName={profileName}
        setProfileName={setProfileName}
        profileEmail={profileEmail}
        profileCity={profileCity}
        setProfileCity={setProfileCity}
        profileCountry={profileCountry}
        setProfileCountry={setProfileCountry}
        savingProfile={savingProfile}
        saveProfile={saveProfile}
      />

      {/* Mealtimes Configuration */}
      <MealtimesDialog
        userMealTimes={userMealTimes}
        setUserMealTimes={setUserMealTimes}
        customMealName={customMealName}
        setCustomMealName={setCustomMealName}
        savingMealTimes={savingMealTimes}
        setSavingMealTimes={setSavingMealTimes}
      />

      {/* Body Weight & Info */}
      <BodyInfoDialog
        newBodyweight={newBodyweight}
        setNewBodyweight={setNewBodyweight}
        weightUnit={weightUnit}
        setWeightUnit={setWeightUnit}
        waistMeasurement={waistMeasurement}
        setWaistMeasurement={setWaistMeasurement}
        waistUnit={waistUnit}
        setWaistUnit={setWaistUnit}
        bodyHeight={bodyHeight}
        setBodyHeight={setBodyHeight}
        heightUnit={heightUnit}
        setHeightUnit={setHeightUnit}
        userAge={userAge}
        setUserAge={setUserAge}
        userGender={userGender}
        setUserGender={setUserGender}
        strideLength={strideLength}
        setStrideLength={setStrideLength}
        medicationsEnabled={medicationsEnabled}
        setMedicationsEnabled={setMedicationsEnabled}
        weightGoal={weightGoal}
        setWeightGoal={setWeightGoal}
        weightGoalStartDate={weightGoalStartDate}
        setWeightGoalStartDate={setWeightGoalStartDate}
        diabetesEnabled={diabetesEnabled}
        setDiabetesEnabled={setDiabetesEnabled}
        diabetesUnit={diabetesUnit}
        setDiabetesUnit={setDiabetesUnit}
        diabetesInsulin={diabetesInsulin}
        setDiabetesInsulin={setDiabetesInsulin}
        diabetesType={diabetesType}
        setDiabetesType={setDiabetesType}
        onSave={onUpdateBodyweight}
      />

      {/* Wearables - next to Body Info */}
      <button 
        onClick={onNavigateWearables}
        className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50 relative"
        data-testid="wearables-btn"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
          <Watch className="h-5 w-5 text-white" />
        </div>
        <span className="text-sm text-white">Wearables</span>
        {isExpired && (
          <Crown className="absolute top-1 right-1 h-4 w-4 text-amber-400" />
        )}
      </button>
      
      {/* Data & Privacy (GDPR) */}
      <DataPrivacyDialog
        loadingDataSummary={loadingDataSummary}
        dataSummary={dataSummary}
        onLoadDataSummary={onLoadDataSummary}
        downloadingData={downloadingData}
        onDownloadAllData={onDownloadAllData}
        emailingPolicy={emailingPolicy}
        onEmailGdprPolicy={onEmailGdprPolicy}
        onDownloadGdprPdf={onDownloadGdprPdf}
        deletingData={deletingData}
        onDeleteAllData={onDeleteAllData}
      />
      
      {/* Security (2FA, Devices, Login History) */}
      <SecurityDialog />
      
      {/* Subscription - next to Invoices */}
      <SubscriptionDialog />
      
      {/* PDF Invoices - next to Premium */}
      <InvoicesDialog
        paymentHistory={paymentHistory}
        loadingHistory={loadingHistory}
        downloadingInvoice={downloadingInvoice}
        onLoadHistory={onLoadHistory}
        onDownloadInvoice={onDownloadInvoice}
      />
      
      {/* Theme Settings - next to Logout */}
      <ThemeDialog />
      
      {/* Logout - same style as other buttons */}
      <button 
        onClick={onLogout}
        className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50"
        data-testid="logout-btn"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
          <LogOut className="h-5 w-5 text-white" />
        </div>
        <span className="text-sm text-white">Logout</span>
      </button>
    </div>
  );
};

export default PersonalDialogContent;
